import { ClassDefinition, ToolbarType, ScopeType } from '../components/ClassEditor';
import { isBuiltinSelector } from './builtin-selectors';

export type ClassMap = Record<string, ClassDefinition>;

const SCSS_BREAKERS = /\/\*|\*\/|[{};"'\\]|[\r\n]/g;

export const scssSafe = (value: string): string => (value ?? '').replace(SCSS_BREAKERS, '').trim();

const SAFE_DECLARATION_VALUE = /^[a-zA-Z0-9\u00C0-\uFFEF\s._,%#+\-()/]*$/;

const parensBalanced = (value: string): boolean => {
  let depth = 0;
  for (const char of value) {
    if (char === '(') depth += 1;
    if (char === ')') {
      depth -= 1;
      if (depth < 0) return false;
    }
  }
  return depth === 0;
};

export const scssSafeValue = (value: string): string => {
  const stripped = scssSafe(value);
  return SAFE_DECLARATION_VALUE.test(stripped) && parensBalanced(stripped) ? stripped : '';
};

export const entryLabelValue = (labelSv: string, labelEn: string): string => `SV:${scssSafe(labelSv)} EN:${scssSafe(labelEn)}`;

const CUSTOM_DECLARATION = /^\s*([a-zA-Z-]+)\s*:/;

// Properties the author declared at the top level of Egen CSS. Those belong to the
// author, not to the form, and the form must not claim them as its own values.
export function customCssOwnedProperties(customCss?: string): Set<string> {
  const owned = new Set<string>();
  if (!customCss) return owned;

  let depth = 0;
  let inComment = false;

  for (const rawLine of customCss.split('\n')) {
    let line = rawLine;

    if (inComment) {
      const closed = line.indexOf('*/');
      if (closed < 0) continue;
      line = line.slice(closed + 2);
      inComment = false;
    }

    const code = line.replace(/\/\*[\s\S]*?\*\//g, '').replace(/\/\/.*$/, '');
    const opened = code.lastIndexOf('/*');
    let visible = code;
    if (opened >= 0) {
      inComment = true;
      visible = code.slice(0, opened);
    }

    if (depth === 0) {
      const match = visible.match(CUSTOM_DECLARATION);
      if (match) owned.add(match[1]);
    }

    depth = Math.max(0, depth + (visible.match(/{/g) || []).length - (visible.match(/}/g) || []).length);
  }

  return owned;
}

const DEPRECATED_ELEMENTS = new Set(['ul', 'ol', 'li']);

const FORM_PROPERTY_KEYS = new Set([
  'font-family',
  'font-size',
  'line-height',
  'letter-spacing',
  'font-weight',
  'color',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
]);

export interface ClassMapStyleOutput {
  style: Record<string, Record<string, string>>;
  rawScss: string;
}

export function classMapToStyle(classMap: ClassMap): ClassMapStyleOutput {
  const style: Record<string, Record<string, string>> = {};
  const rawBlocks: string[] = [];

  for (const [className, def] of Object.entries(classMap)) {
    const nonEmptyProps: Record<string, string> = {};
    for (const [prop, val] of Object.entries(def.properties)) {
      if (val.trim()) {
        const safe = scssSafeValue(val);
        if (safe) {
          nonEmptyProps[prop] = prop === 'font-family' ? `"${safe}"` : safe;
        }
      }
    }

    style[className] = nonEmptyProps;

    const hasPageScope = def.scopes?.includes('page');

    // Collect raw SCSS blocks (custom CSS + font variants) for this class
    const nestedLines: string[] = [];

    const boldFamily = scssSafeValue(def.boldFontFamily ?? '');
    const italicFamily = scssSafeValue(def.italicFontFamily ?? '');
    if (boldFamily) {
      nestedLines.push(`  .xrp-body-bold, .xrp-heading-bold, strong, b { font-family: "${boldFamily}"; }`);
    }
    if (italicFamily) {
      nestedLines.push(`  .xrp-body-italic, .xrp-heading-italic, em, i { font-family: "${italicFamily}"; }`);
    }
    if (def.customCss?.trim()) {
      // `& { … }` compiles to the same selector but sits a level deeper, where scssToJson
      // stops reading. Without it the author's declarations come back as form values.
      nestedLines.push(`  & {\n${def.customCss.trim()}\n  }`);
    }

    if (hasPageScope) {
      const pageLines = [...Object.entries(nonEmptyProps).map(([prop, val]) => `  ${prop}: ${val};`), ...nestedLines];
      if (pageLines.length > 0) {
        rawBlocks.push(`&.${className} {\n${pageLines.join('\n')}\n}`);
      }
    }

    if (nestedLines.length > 0) {
      // Descendant selector for table/child usage
      const descendantSelectors = def.elements.length > 0 ? def.elements.map((el) => `${el}.${className}`).join(', ') : `.${className}`;
      rawBlocks.push(`${descendantSelectors} {\n${nestedLines.join('\n')}\n}`);
    }
  }

  return { style, rawScss: rawBlocks.join('\n\n') };
}

export function styleToClassMap(
  style: Record<string, unknown>,
  metadata?: Record<
    string,
    {
      elements: string[];
      labelSv: string;
      labelEn: string;
      comment?: string;
      customCss?: string;
      boldFontFamily?: string;
      italicFontFamily?: string;
      toolbars?: ToolbarType[];
      scopes?: ScopeType[];
    }
  >
): ClassMap {
  const map: ClassMap = {};
  if (!style) return map;

  for (const [className, value] of Object.entries(style)) {
    if (className === 'toolbar' || className === 'financial-toolbar') continue;
    if (isBuiltinSelector(className)) continue;
    if (typeof value !== 'object' || value === null) continue;

    const meta = metadata?.[className];
    const isLegacyClass = !meta?.customCss;
    const customOwned = customCssOwnedProperties(meta?.customCss);

    const properties: Record<string, string> = {};
    const leftover: string[] = [];

    for (const [prop, val] of Object.entries(value as Record<string, unknown>)) {
      let str: string;
      if (typeof val === 'object' && val !== null && 'value' in val && 'unit' in val) {
        str = `${val.value}${val.unit}`;
      } else {
        str = val == null ? '' : String(val);
      }

      if (customOwned.has(prop)) {
        continue;
      }

      if (FORM_PROPERTY_KEYS.has(prop)) {
        let formValue = str;
        if ((formValue.startsWith('"') && formValue.endsWith('"')) || (formValue.startsWith("'") && formValue.endsWith("'"))) {
          formValue = formValue.slice(1, -1);
        }
        properties[prop] = formValue;
      } else if (isLegacyClass) {
        leftover.push(`${prop}: ${str};`);
      }
    }

    map[className] = {
      elements: (meta?.elements ?? []).filter((el) => !DEPRECATED_ELEMENTS.has(el)),
      properties,
      labelSv: meta?.labelSv ?? className,
      labelEn: meta?.labelEn ?? className,
      comment: meta?.comment ?? '',
      customCss: meta?.customCss ?? leftover.join('\n'),
      boldFontFamily: meta?.boldFontFamily ?? '',
      italicFontFamily: meta?.italicFontFamily ?? '',
      toolbars: meta?.toolbars ?? ['toolbar'],
      scopes: meta?.scopes,
    };
  }

  return map;
}

export interface ExistingToolbarEntries {
  toolbar?: Record<string, string>;
  financialToolbar?: Record<string, string>;
}

export interface ExistingScopeEntries {
  pageClasses?: Record<string, string>;
  tableClasses?: Record<string, string>;
}

function mergeListEntries(
  existing: Record<string, string> | undefined,
  wanted: Array<[string, string]>,
  owns: (key: string) => boolean
): Record<string, string> {
  const merged: Record<string, string> = { ...(existing ?? {}) };
  const wantedKeys = new Set(wanted.map(([key]) => key));

  for (const key of Object.keys(merged)) {
    if (owns(key) && !wantedKeys.has(key)) {
      delete merged[key];
    }
  }
  for (const [key, label] of wanted) {
    merged[key] = label;
  }

  return merged;
}

const selectorsFor = (className: string, def: ClassDefinition): string[] =>
  def.elements.length === 0 ? [`.${className}`] : def.elements.map((el) => `${el}.${className}`);

const labelFor = (def: ClassDefinition): string => entryLabelValue(def.labelSv, def.labelEn);

const ownsClassKey = (key: string) => key.includes('.');

export type ListKey = 'toolbar' | 'financial-toolbar' | 'page' | 'table';

export type ResolvedLists = Record<ListKey, Record<string, string>>;

export function resolveLists(classMap: ClassMap, existingToolbar?: ExistingToolbarEntries, existingScope?: ExistingScopeEntries): ResolvedLists {
  const wanted: Record<ListKey, Array<[string, string]>> = { toolbar: [], 'financial-toolbar': [], page: [], table: [] };

  for (const [className, def] of Object.entries(classMap)) {
    const toolbars = def.toolbars ?? ['toolbar'];
    for (const selector of selectorsFor(className, def)) {
      for (const bar of toolbars) {
        wanted[bar].push([selector, labelFor(def)]);
      }
    }
    const scopes = def.scopes ?? [];
    if (scopes.includes('page')) wanted.page.push([className, labelFor(def)]);
    if (scopes.includes('table')) wanted.table.push([className, labelFor(def)]);
  }

  return {
    toolbar: mergeListEntries(existingToolbar?.toolbar, wanted.toolbar, ownsClassKey),
    'financial-toolbar': mergeListEntries(existingToolbar?.financialToolbar, wanted['financial-toolbar'], ownsClassKey),
    page: mergeListEntries(existingScope?.pageClasses, wanted.page, () => true),
    table: mergeListEntries(existingScope?.tableClasses, wanted.table, () => true),
  };
}

export function toolbarCommentFrom(lists: ResolvedLists): string {
  const blocks: string[] = [];
  if (Object.keys(lists.toolbar).length > 0) {
    blocks.push(`.toolbar {\n  /*! ${JSON.stringify(lists.toolbar)} */\n}`);
  }
  if (Object.keys(lists['financial-toolbar']).length > 0) {
    blocks.push(`.financial-toolbar {\n  /*! ${JSON.stringify(lists['financial-toolbar'])} */\n}`);
  }
  return blocks.join('\n');
}

export function scopeCommentFrom(lists: ResolvedLists): string {
  const comments: string[] = [];
  if (Object.keys(lists.page).length > 0) {
    comments.push(`/*! custom-page-classes: ${JSON.stringify(lists.page)} */`);
  }
  if (Object.keys(lists.table).length > 0) {
    comments.push(`/*! custom-table-classes: ${JSON.stringify(lists.table)} */`);
  }
  return comments.join('\n');
}

export function generateToolbarComment(classMap: ClassMap, existingEntries?: ExistingToolbarEntries): string {
  return toolbarCommentFrom(resolveLists(classMap, existingEntries));
}

export function generateScopeComments(classMap: ClassMap, existingEntries?: ExistingScopeEntries): string {
  return scopeCommentFrom(resolveLists(classMap, undefined, existingEntries));
}

interface ClassMetadataEntry {
  elements: string[];
  labelSv: string;
  labelEn: string;
  comment?: string;
  customCss?: string;
  boldFontFamily?: string;
  italicFontFamily?: string;
  toolbars?: ToolbarType[];
  scopes?: ScopeType[];
}

export function extractClassMetadata(classMap: ClassMap): Record<string, ClassMetadataEntry> {
  const metadata: Record<string, ClassMetadataEntry> = {};
  for (const [className, def] of Object.entries(classMap)) {
    metadata[className] = {
      elements: def.elements,
      labelSv: def.labelSv,
      labelEn: def.labelEn,
    };
    if (def.comment) metadata[className].comment = def.comment;
    if (def.customCss) metadata[className].customCss = def.customCss;
    if (def.boldFontFamily) metadata[className].boldFontFamily = def.boldFontFamily;
    if (def.italicFontFamily) metadata[className].italicFontFamily = def.italicFontFamily;
    if (def.toolbars) metadata[className].toolbars = def.toolbars;
    if (def.scopes && def.scopes.length > 0) metadata[className].scopes = def.scopes;
  }
  return metadata;
}
